import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { Barra, Carregando } from '@/componentes/Esqueleto';
import { Medida } from '@/componentes/Medida';
import { HistoriaNaMesa } from '@/historias/mesa';
import { formatarAteDuasCasas, formatarData, formatarDataHora, formatarDecimal, formatarInteiro } from '@/lib/formato';
import { ehSemPermissao, fraseDoSemPermissao, perfilAPedir } from '@/lib/perfil-da-licenca';

import type { EstadoDoVencimento, Gatilho } from '@/modulos/manutencao/api';
import { corDoEstado, ROTULO_DO_ESTADO, textoDoFaltam } from '@/modulos/manutencao/vencimento';
import type { Etapa, TipoDeOrdem } from '@/modulos/oficina/api';
import { numeroDaOrdem, ROTULO_DA_ETAPA, ROTULO_DO_TIPO } from '@/modulos/oficina/ordem';
import { momentoDaParada, ROTULO_DO_MOMENTO, textoDaJanela, textoDeQuemCombinou } from '@/modulos/oficina/parada';

import { FormularioLeitura } from './FormularioLeitura';
import { ORIGEM_DA_LEITURA, SITUACAO_DO_DOCUMENTO, type Prontuario, type Secao, type TipoDeMedidor } from './frota';
import { TelemetriaNaFicha } from '@/modulos/telemetria/TelemetriaNaFicha';
import { PerguntaAoManual } from './PerguntaAoManual';

const ehEstado = (e: string): e is EstadoDoVencimento => Object.hasOwn(ROTULO_DO_ESTADO, e);
const ehTipo = (t: string): t is TipoDeOrdem => Object.hasOwn(ROTULO_DO_TIPO, t);
const ehEtapa = (e: string): e is Etapa => Object.hasOwn(ROTULO_DA_ETAPA, e);
const ehGatilho = (g: string): g is Gatilho => g === 'horas' || g === 'km' || g === 'dias';

/** Os blocos depois do medidor, na ordem da ficha: o titulo nao depende da resposta. */
const BLOCOS_DO_USO = [
  'Telemetria',
  'Horas nos últimos 30 dias',
  'Abastecimentos nos últimos 30 dias',
  'Manutenção preventiva',
  'Ordens de serviço',
  'Documentos técnicos',
] as const;

/**
 * O que os OUTROS modulos sabem desta maquina, no topo da ficha.
 *
 * O v6 define a ferramenta 2 do modulo 02 como o lugar que junta "horas,
 * abastecimentos, OS, pecas, documentos e custos". Quatro desses existem hoje —
 * horas (03), abastecimentos (08), O.S. (06) e documentos (12) —, mais a
 * preventiva (05), e entram aqui. A PARADA PROGRAMADA com a operacao (0089)
 * vai no bloco das O.S. A PECA APLICADA e o CUSTO DA MAQUINA sairam daqui na
 * R06 (p.71): sao os dois blocos abaixo dos dados da ficha
 * (`CustoEPecasDaFicha.tsx`).
 *
 * Nao e aba propria porque o painel ja tem as quatro abas que o manual permite
 * (p.19). E a ficha e o que se abre primeiro.
 *
 * Cada secao obedece a permissao do proprio modulo: sem ela, a secao diz isso
 * e nao mostra nada — em vez de fingir que a maquina nao tem abastecimento.
 */
export function UsoNoProntuario({
  equipamentoId,
  tipoMedidor,
  prontuario,
  carregando,
  erro,
  primeiro,
}: {
  readonly equipamentoId: string;
  /** O medidor da ficha: caminhao le odometro, e o bloco escreve km. */
  readonly tipoMedidor: TipoDeMedidor;
  readonly prontuario: Prontuario | undefined;
  readonly carregando: boolean;
  /** A recusa do prontuario: o 403 e o estado sem permissao, e nao "nao carregaram". */
  readonly erro?: unknown;
  /**
   * A primeira celula da grade, antes do medidor: a foto da maquina, na ficha
   * (pedido do dono, 31/10/2026). Os blocos vivem na `grade-do-painel`, que e
   * uma coluna no painel estreito e tres na janela expandida.
   */
  readonly primeiro?: ReactNode;
}) {
  /* SEM `prontuario.ler` (prova do marco, 07/10/2026): o perfil a pedir, e nao
     "nao carregaram, tente de novo" — tentar de novo nao concede licenca. */
  if (!carregando && !prontuario && ehSemPermissao(erro)) {
    return (
      <div className="grade-do-painel">
        {primeiro}
        <p className="campo-largo txt-apoio text-tinta-secundaria">
          {`Você não tem acesso ao prontuário da máquina: horas, abastecimentos, documentos e O.S. ${fraseDoSemPermissao(erro, 'o prontuário')}`}
        </p>
      </div>
    );
  }
  /* Falhou: o esqueleto nao pode ficar para sempre — esqueleto que nunca
     termina diz "esta vindo" de algo que nao vem. O motivo esta na aba
     Historico, que mostra o erro do mesmo prontuario. */
  if (!carregando && !prontuario) {
    return (
      <div className="grade-do-painel">
        {primeiro}
        <p className="campo-largo txt-apoio text-tinta-secundaria">
          Horas, abastecimentos e documentos não carregaram. A aba Histórico diz o motivo; feche e abra a máquina para tentar de novo.
        </p>
      </div>
    );
  }
  if (carregando || !prontuario) {
    /* Carregando (p.32) na forma da secao: o titulo de cada bloco ja se sabe,
       e so o que cada modulo diz da maquina vira linha. */
    return (
      <section className="grade-do-painel" aria-label="Uso da máquina">
        {primeiro}
        <Carregando oQue="Carregando o uso da máquina" className="contents">
          <div aria-hidden="true" className="contents">
            {[tipoMedidor === 'odometro' ? 'Odômetro atual' : 'Horímetro atual', ...BLOCOS_DO_USO].map((titulo, i) => (
              <Bloco key={titulo} titulo={titulo}>
                <Barra className={`my-1 h-3 ${i === 0 ? 'w-24' : i % 2 === 0 ? 'w-2/5' : 'w-1/2'}`} />
                <Barra className={`my-1 h-3 ${i % 2 === 0 ? 'w-3/5' : 'w-3/4'}`} />
              </Bloco>
            ))}
          </div>
        </Carregando>
      </section>
    );
  }

  const { horimetro, horas, abastecimentos, documentos, manutencao, ordens } = prontuario;

  return (
    <section className="grade-do-painel" aria-label="Uso da máquina">
      {primeiro}
      <HistoriaNaMesa codigo="COL-EQP-02" className="min-w-0">
        <Bloco titulo={tipoMedidor === 'odometro' ? 'Odômetro atual' : 'Horímetro atual'}>
          {horimetro ? (
            <>
              <Medida valor={formatarAteDuasCasas(horimetro.valor)} unidade={horimetro.tipoMedidor === 'odometro' ? 'km' : 'h'} />
              <span className="txt-apoio text-tinta-apagada">
                {horimetro.lidaEm ? `Lido em ${formatarDataHora(horimetro.lidaEm)}` : 'Sem data de leitura'}
                {` · ${ORIGEM_DA_LEITURA[horimetro.origem] ?? horimetro.origem}`}
              </span>
              {/* AS ÚLTIMAS LEITURAS, cada uma com a origem (0103): com a
                  telemetria entrando, a pergunta "de onde veio este número"
                  é a primeira. */}
              {(prontuario.leituras ?? []).length > 1 ? (
                <ul aria-label="Últimas leituras do medidor" className="mt-1 flex flex-col">
                  {(prontuario.leituras ?? []).map((l, i) => (
                    <li key={`${String(l.lidaEm?.getTime() ?? i)}-${l.origem}`} className="grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-3 border-t border-borda-sutil py-1">
                      <span className="txt-apoio text-tinta-secundaria">
                        {`${l.lidaEm ? formatarDataHora(l.lidaEm) : 'Sem data'} · ${ORIGEM_DA_LEITURA[l.origem] ?? l.origem}`}
                      </span>
                      <Medida valor={formatarAteDuasCasas(l.valor)} unidade={l.tipoMedidor === 'odometro' ? 'km' : 'h'} />
                    </li>
                  ))}
                </ul>
              ) : null}
            </>
          ) : (
            <span className="txt-apoio text-tinta-apagada">
              Nenhuma leitura registrada. Ela entra com o primeiro apontamento, abastecimento ou pela leitura inicial
              abaixo.
            </span>
          )}
          <FormularioLeitura equipamentoId={equipamentoId} tipoMedidor={tipoMedidor} temLeitura={horimetro !== null} />
        </Bloco>
      </HistoriaNaMesa>

      <Bloco titulo="Telemetria">
        <TelemetriaNaFicha equipamentoId={equipamentoId} />
      </Bloco>

      <Bloco titulo="Horas nos últimos 30 dias">
        <Fechada secao={horas} modulo="operação" licenca="operacao.ler">
          {(h) =>
            h.ultimos30Dias.apontamentos === 0 ? (
              <span className="txt-apoio text-tinta-apagada">Nenhum turno apontado nos últimos 30 dias.</span>
            ) : (
              <>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-1">
                  <Par rotulo="Motor ligado" valor={h.ultimos30Dias.motor} />
                  <Par rotulo="Produção" valor={h.ultimos30Dias.producao} />
                  <Par rotulo="Espera" valor={h.ultimos30Dias.espera} />
                  <Par rotulo="Deslocamento" valor={h.ultimos30Dias.deslocamento} />
                  <Par rotulo="Manutenção" valor={h.ultimos30Dias.manutencao} />
                </dl>
                <span className="txt-apoio text-tinta-apagada">
                  {`${turnos(h.ultimos30Dias.apontamentos)}. Motor ligado corre junto com as outras e não se soma a elas.`}
                </span>
                {h.operadores.length > 0 ? (
                  <div className="flex flex-col gap-1 border-t border-borda-sutil pt-2">
                    <span className="txt-legenda font-normal text-tinta-secundaria">Quem operou</span>
                    <dl className="grid grid-cols-2 gap-x-4 gap-y-1">
                      {h.operadores.map((o) => (
                        <div key={o.nome ?? '(sem operador)'} className="contents">
                          <dt className="txt-corpo text-tinta-secundaria">
                            {o.nome ?? 'Sem operador no apontamento'}
                            <span className="txt-linha2 block text-tinta-secundaria">
                              {`${turnos(o.apontamentos)}${o.ultimoDia ? ` · último em ${formatarData(new Date(`${o.ultimoDia}T12:00:00`))}` : ''}`}
                            </span>
                          </dt>
                          <dd className="text-right">
                            <Medida valor={formatarDecimal(o.horasMotor, 1)} unidade="h" />
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                ) : null}
              </>
            )
          }
        </Fechada>
      </Bloco>

      <Bloco titulo="Abastecimentos nos últimos 30 dias">
        <Fechada secao={abastecimentos} modulo="combustível" licenca="combustivel.ler">
          {(a) =>
            a.ultimos30DiasPorProduto.length === 0 ? (
              <span className="txt-apoio text-tinta-apagada">Nenhum abastecimento nos últimos 30 dias.</span>
            ) : (
              <>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-1">
                  {/* POR PRODUTO, nunca somado: ARLA não é litro de diesel. */}
                  {a.ultimos30DiasPorProduto.map((p) => (
                    <div key={p.produto} className="contents">
                      <dt className="txt-corpo text-tinta-secundaria">{p.produto}</dt>
                      <dd className="text-right">
                        <Medida valor={formatarDecimal(p.litros, 1)} unidade="L" />
                      </dd>
                    </div>
                  ))}
                </dl>
                {a.ultimo ? (
                  <span className="txt-apoio text-tinta-apagada">{`Último em ${formatarDataHora(a.ultimo)}.`}</span>
                ) : null}
              </>
            )
          }
        </Fechada>
      </Bloco>

      <Bloco titulo="Manutenção preventiva">
        <Fechada secao={manutencao} modulo="manutenção" licenca="manutencao.ler">
          {(m) =>
            m.itens.length === 0 ? (
              <span className="txt-apoio text-tinta-apagada">
                Nenhum plano preventivo publicado alcança esta máquina.
              </span>
            ) : (
              <ul className="flex flex-col gap-2">
                {m.itens.map((v) => (
                  <li key={v.tarefaId} className="flex flex-col">
                    <span className="txt-corpo text-tinta">{v.descricao}</span>
                    {/* R02: vencida em vermelho, próxima em âmbar. */}
                    <span className={`txt-apoio ${corDoEstado(v.estado) ?? 'text-tinta-apagada'}`}>
                      {[
                        /* As frases da tela de manutenção, e não uma cópia delas:
                           `vencimento.ts` é puro e tem teste. */
                        ehEstado(v.estado) ? ROTULO_DO_ESTADO[v.estado] : v.estado,
                        ehGatilho(v.gatilho) ? textoDoFaltam({ faltam: v.faltam, gatilho: v.gatilho }) : null,
                        v.plano,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </li>
                ))}
                <li>
                  <Link to="/manutencao" className="botao botao-terciario">
                    Abrir manutenção
                  </Link>
                </li>
              </ul>
            )
          }
        </Fechada>
      </Bloco>

      <Bloco titulo="Ordens de serviço">
        <Fechada secao={ordens} modulo="oficina" licenca="os.ler">
          {(o) => (
            <>
              {/* A PARADA PROGRAMADA (0089): quando a máquina sai da operação, e
                  com quem foi combinado. Antes da lista: é o que a frente pergunta. */}
              {o.paradasProgramadas.map((p) =>
                p.de && p.ate ? (
                  <div key={p.ordemId} className="flex flex-col">
                    <span className="txt-corpo text-tinta">{`Parada programada · ${textoDaJanela(p.de, p.ate)}`}</span>
                    <span
                      className={`txt-apoio ${momentoDaParada(p.de, p.ate) === 'atrasada' ? 'text-parada' : 'text-tinta-apagada'}`}
                    >
                      {[
                        numeroDaOrdem(p.numero),
                        momentoDaParada(p.de, p.ate) === 'programada' ? null : ROTULO_DO_MOMENTO[momentoDaParada(p.de, p.ate)],
                        textoDeQuemCombinou(p.combinadaCom, p.combinadaPor),
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </div>
                ) : null,
              )}
              {o.itens.length === 0 ? (
                <span className="txt-apoio text-tinta-apagada">Nenhuma O.S. aberta para esta máquina.</span>
              ) : (
                <ul className="flex flex-col gap-2">
                  {o.itens.map((os) => (
                    <li key={os.id} className="flex flex-col">
                      <span className="txt-corpo text-tinta">{`${numeroDaOrdem(os.numero)} · ${os.sintoma}`}</span>
                      <span className="txt-apoio text-tinta-apagada">
                        {[
                          ehTipo(os.tipo) ? ROTULO_DO_TIPO[os.tipo] : os.tipo,
                          ehEtapa(os.etapa) ? ROTULO_DA_ETAPA[os.etapa] : os.etapa,
                          os.maquinaParada ? 'máquina parada' : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    </li>
                  ))}
                  <li>
                    <Link to="/ordens" className="botao botao-terciario">
                      Abrir oficina
                    </Link>
                  </li>
                </ul>
              )}
            </>
          )}
        </Fechada>
      </Bloco>

      <Bloco titulo="Documentos técnicos">
        <Fechada secao={documentos} modulo="biblioteca técnica" licenca="documento_tecnico.ler">
          {(d) =>
            d.itens.length === 0 ? (
              <span className="txt-apoio text-tinta-apagada">
                Nenhum documento alcança esta máquina. Aplique um manual ao fabricante, ao modelo ou à unidade na
                biblioteca.
              </span>
            ) : (
              <ul className="flex flex-col gap-2">
                {d.itens.map((doc) => (
                  <li key={doc.documentoId} className="flex flex-col">
                    <span className="txt-corpo text-tinta">{doc.titulo}</span>
                    <span className="txt-apoio text-tinta-apagada">
                      {[
                        SITUACAO_DO_DOCUMENTO[doc.situacao] ?? doc.situacao,
                        `vale para ${doc.alcancaPor}`,
                        doc.temTexto ? 'texto pesquisável' : 'sem texto pesquisável',
                      ].join(' · ')}
                    </span>
                  </li>
                ))}
                <li>
                  <Link to="/tecnico" className="botao botao-consulta">
                    Abrir biblioteca
                  </Link>
                </li>
              </ul>
            )
          }
        </Fechada>
        {/* IA 01 (decisão 18): perguntar aos documentos que alcançam a máquina.
            Some sozinho para quem não tem `ia.perguntar`. */}
        <PerguntaAoManual equipamentoId={equipamentoId} />
      </Bloco>

    </section>
  );
}

/** "1 turno", "3 turnos": o plural concorda com o numero (p.22), sem "(s)". */
function turnos(quantos: number): string {
  return `${formatarInteiro(quantos)} ${quantos === 1 ? 'turno' : 'turnos'}`;
}



/** Um bloco do uso: uma celula da grade, em cartao rebaixado para a grade se ler como grade. */
function Bloco({ titulo, children }: { readonly titulo: string; readonly children: ReactNode }) {
  return (
    <div className="bloco-rebaixado flex h-full min-w-0 flex-col gap-1 p-4">
      <span className="txt-rotulo text-tinta-secundaria">{titulo}</span>
      {children}
    </div>
  );
}

function Par({ rotulo, valor }: { readonly rotulo: string; readonly valor: number }) {
  return (
    <>
      <dt className="txt-corpo text-tinta-secundaria">{rotulo}</dt>
      <dd className="text-right">
        <Medida valor={formatarDecimal(valor, 1)} unidade="h" />
      </dd>
    </>
  );
}

/** Sem a permissao do modulo, a secao diz isso — e nao "nenhum registro". */
function Fechada<T>({
  secao,
  modulo,
  licenca,
  children,
}: {
  readonly secao: Secao<T>;
  readonly modulo: string;
  /** A leitura do modulo: o perfil a pedir sai dela (p.34). */
  readonly licenca: string;
  readonly children: (aberta: T) => ReactNode;
}) {
  if (!secao.permitido) {
    return (
      <span className="txt-apoio text-tinta-apagada">
        {`Seu perfil não vê o módulo de ${modulo}. Peça ao administrador da sua empresa o perfil ${perfilAPedir(licenca)}.`}
      </span>
    );
  }
  return <>{children(secao)}</>;
}
